import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import {
  canonicalAccountSchema,
  type CanonicalAccount,
} from './useAccounts';
import { canonicalTransactionRowSchema, type CanonicalTransactionRow } from './useTransactions';

export { canonicalAccountSchema, canonicalTransactionRowSchema };
export type { CanonicalAccount, CanonicalTransactionRow };

const uuidSchema = z.string().uuid();
const generationSchema = z.number().int().nonnegative();
const existingVersionSchema = z.number().int().positive();
const decimalStringSchema = z.string().regex(/^[+-]?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/);
const fingerprintSchema = z.string().regex(/^sha256:[0-9a-f]{64}$/);
const decisionStatusSchema = z.enum(['review_needed', 'same', 'distinct']);
const sourceStateSchema = z.enum(['active', 'retained', 'manual']);

export const annotationPreviewLineSchema = z.object({
  line_item_id: uuidSchema,
  amount: decimalStringSchema,
  category_id: uuidSchema.nullable(),
  category_slug: z.string().nullable(),
  is_business: z.boolean(),
  tags: z.array(z.string()),
  version: generationSchema,
}).strict();

const annotationPreviewSummarySchema = z.object({
  line_item_count: generationSchema,
  has_splits: z.boolean(),
  has_user_categories: z.boolean(),
  has_business_annotations: z.boolean(),
}).strict();

export const annotationOptionSchema = z.object({
  anchor_transaction_id: uuidSchema,
  currency: z.string(),
  fingerprint: fingerprintSchema,
  summary: annotationPreviewSummarySchema,
  preview_line_items: z.array(annotationPreviewLineSchema),
}).strict();

export const accountPairSchema = z.object({
  old_account_id: uuidSchema,
  new_account_id: uuidSchema,
  old_source_state: sourceStateSchema,
  new_source_state: sourceStateSchema,
  old_display_name: z.string(),
  new_display_name: z.string(),
  decision_id: uuidSchema.nullable(),
  decision: decisionStatusSchema.nullable(),
  decision_version: existingVersionSchema.nullable(),
  expected_generation: generationSchema.nullable(),
  can_decide: z.boolean(),
  // Public uncertainty only, always present: `auto_match_uncertain` when the
  // automatic rule could not settle this replacement account, else null. No
  // score or provider evidence crosses the wire, and an omitted key is drift.
  reason: z.enum(['auto_match_uncertain']).nullable(),
}).strict();

const candidateTransactionRowSchema = z.object({
  transaction_id: uuidSchema,
  posted_at: z.string().datetime({ offset: true }),
  amount: decimalStringSchema,
  merchant_display: z.string(),
}).strict();

export const transactionPairSchema = z.object({
  candidate_id: uuidSchema,
  old_transaction_id: uuidSchema,
  new_transaction_id: uuidSchema,
  old_source_tip_id: uuidSchema,
  new_source_tip_id: uuidSchema,
  decision_id: uuidSchema.nullable(),
  decision: decisionStatusSchema,
  decision_version: existingVersionSchema.nullable(),
  expected_generation: generationSchema.nullable(),
  old_row: candidateTransactionRowSchema,
  new_row: candidateTransactionRowSchema,
  requires_annotation_anchor: z.boolean(),
  annotation_options: z.array(annotationOptionSchema).max(2),
}).strict().superRefine((value, ctx) => {
  if (value.requires_annotation_anchor !== (value.annotation_options.length === 2)) {
    ctx.addIssue({ code: 'custom', message: 'requires_annotation_anchor must be true iff there are two annotation choices' });
  }
});

export const decisionSchema = z.object({
  decision_id: uuidSchema,
  kind: z.enum(['account', 'transaction']),
  decision: z.enum(['same', 'distinct', 'reversed']),
  decision_version: existingVersionSchema,
  review_generation: generationSchema,
  canonical_account_id: uuidSchema,
  canonical_transaction_id: uuidSchema.nullable(),
  old_source_tip_id: uuidSchema.nullable(),
  new_source_tip_id: uuidSchema.nullable(),
  annotation_anchor_transaction_id: uuidSchema.nullable(),
}).strict();

export const reviewStatusSchema = z.object({
  replacement_item_id: uuidSchema,
  review_generation: generationSchema.nullable(),
  review_status: z.enum(['needs_review', 'finished']).nullable(),
  unresolved_pair_count: generationSchema.nullable(),
  unresolved_account_pair_count: generationSchema.nullable(),
  unresolved_transaction_pair_count: generationSchema.nullable(),
  provider_history_status: z.enum(['loading', 'initial_complete', 'historical_complete', 'failed']).nullable(),
  provider_history_status_at: z.string().datetime({ offset: true }).nullable(),
}).strict();

export const accountDecisionSchema = z.object({
  old_account_id: uuidSchema,
  new_account_id: uuidSchema,
  decision: z.enum(['same', 'distinct']),
  expected_version: existingVersionSchema.nullable(),
  expected_generation: generationSchema,
}).strict();

const accountDecisionCommandSchema = accountDecisionSchema.omit({ expected_generation: true });
export const accountDecisionBatchSchema = z.object({
  expected_generation: generationSchema,
  decisions: z.array(accountDecisionCommandSchema).min(1).max(20),
}).strict();

const transactionDecisionCommandSchema = z.object({
  old_transaction_id: uuidSchema,
  new_transaction_id: uuidSchema,
  decision: z.enum(['same', 'distinct']),
  expected_version: existingVersionSchema.nullable(),
  old_source_tip_id: uuidSchema,
  new_source_tip_id: uuidSchema,
  annotation_anchor_transaction_id: uuidSchema.nullable().optional(),
  annotation_fingerprint: fingerprintSchema.nullable().optional(),
}).strict();

function validateTransactionDecision(value: z.infer<typeof transactionDecisionCommandSchema>, ctx: z.RefinementCtx) {
  if ((value.annotation_anchor_transaction_id == null) !== (value.annotation_fingerprint == null)) {
    ctx.addIssue({ code: 'custom', message: 'annotation anchor and fingerprint must be provided together' });
  }
  if (value.decision === 'distinct' && (value.annotation_anchor_transaction_id != null || value.annotation_fingerprint != null)) {
    ctx.addIssue({ code: 'custom', message: 'distinct decisions cannot select an annotation anchor' });
  }
}

const validatedTransactionDecisionCommandSchema = transactionDecisionCommandSchema.superRefine(validateTransactionDecision);

export const transactionDecisionSchema = transactionDecisionCommandSchema.extend({
  expected_generation: generationSchema,
}).strict().superRefine(validateTransactionDecision);

export const transactionDecisionBatchSchema = z.object({
  expected_generation: generationSchema,
  decisions: z.array(validatedTransactionDecisionCommandSchema).min(1).max(50),
}).strict();

export const reverseDecisionSchema = z.object({
  expected_generation: generationSchema,
  expected_version: existingVersionSchema,
}).strict();

export const manualTransactionPairSchema = z.object({
  old_transaction_id: uuidSchema,
  new_transaction_id: uuidSchema,
  old_source_tip_id: uuidSchema,
  new_source_tip_id: uuidSchema,
  expected_generation: generationSchema,
}).strict();

export const manualPairTipSchema = z.object({
  transaction_id: uuidSchema,
  source_tip_id: uuidSchema,
  account_id: uuidSchema,
  item_id: uuidSchema.nullable(),
  posted_at: z.string().datetime({ offset: true }),
  amount: decimalStringSchema,
  merchant_display: z.string(),
}).strict();

export const finishReviewSchema = z.object({ expected_generation: generationSchema }).strict();

const accountPairsMetaSchema = z.object({
  next_cursor: z.string().nullable(),
  review_generation: generationSchema.nullable(),
  review_status: z.enum(['needs_review', 'finished']).nullable(),
  // Replacement accounts still waiting on an uncertain match, counted across
  // the Item. An empty `data` with a positive count is a pending review, not a
  // finished one -- a cross-institution reconnect lists no candidate pair.
  uncertain_account_match_count: generationSchema,
}).strict();
const transactionCandidatesMetaSchema = z.object({ next_cursor: z.string().nullable() }).strict();
const reviewStatusMetaSchema = z.object({}).strict();
const emptyMutationMetaSchema = z.object({}).strict();
const manualPairOptionsMetaSchema = z.object({
  next_cursor: z.string().nullable(),
  review_generation: generationSchema,
  review_status: z.enum(['needs_review', 'finished']).nullable(),
  side: z.enum(['old', 'new']),
}).strict();
const manualPairMutationMetaSchema = z.object({ review_generation: generationSchema }).strict();
const accountDecisionBatchMetaSchema = z.object({
  review_generation: generationSchema,
  batch_id: uuidSchema,
}).strict();

export const accountPairsResponseSchema = z.object({
  data: z.array(accountPairSchema),
  meta: accountPairsMetaSchema,
}).strict();
export const transactionCandidatesResponseSchema = z.object({
  data: z.array(transactionPairSchema),
  meta: transactionCandidatesMetaSchema,
}).strict();
export const reviewStatusResponseSchema = z.object({
  data: reviewStatusSchema,
  meta: reviewStatusMetaSchema,
}).strict();

export type AccountPair = z.infer<typeof accountPairSchema>;
export type TransactionPair = z.infer<typeof transactionPairSchema>;
export type ReviewStatus = z.infer<typeof reviewStatusSchema>;
export type ManualPairTip = z.infer<typeof manualPairTipSchema>;

export type StaleReselectResult =
  | { kind: 'stale'; reason: 'changed_since' }
  | { kind: 'stale'; reason: 'refresh_failed' };
export type ReconciliationMutationOptions = {
  onStaleReselect: (result: StaleReselectResult) => void;
};
export type AccountDecisionInput = {
  replacementItemId: string;
  pair: AccountPair;
  decision: 'same' | 'distinct';
};
export type AccountDecisionBatchInput = {
  replacementItemId: string;
  selections: Array<{ pair: AccountPair; decision: 'same' | 'distinct' }>;
};
export type ReverseAccountDecisionInput = {
  replacementItemId: string;
  pair: AccountPair;
};
export type FinishReviewInput = {
  replacementItemId: string;
  status: ReviewStatus;
};
export type AnnotationSelection = {
  anchor_transaction_id: string;
  fingerprint: string;
};
export type TransactionDecisionInput = {
  replacementItemId: string;
  pair: TransactionPair;
  decision: 'same' | 'distinct';
  annotationSelection?: AnnotationSelection;
};
export type TransactionDecisionBatchInput = {
  replacementItemId: string;
  selections: Array<Omit<TransactionDecisionInput, 'replacementItemId'>>;
};
export type ReverseTransactionDecisionInput = {
  replacementItemId: string;
  pair: TransactionPair;
};
export type ManualPairSide = 'old' | 'new';
export type ManualPairInput = {
  replacementItemId: string;
  oldTip: ManualPairTip;
  newTip: ManualPairTip;
  expectedGeneration: number;
};

export const reconciliationKeys = {
  root: () => ['reconciliation'] as const,
  accountPairs: (replacementItemId: string, cursor?: string | null) => ['reconciliation', 'account-pairs', replacementItemId, cursor ?? null] as const,
  transactionCandidates: (canonicalAccountId: string, cursor?: string | null) => ['reconciliation', 'transaction-candidates', canonicalAccountId, cursor ?? null] as const,
  reviewStatus: (replacementItemId: string) => ['reconciliation', 'review-status', replacementItemId] as const,
  manualPairOptions: (canonicalAccountId: string, side: ManualPairSide, cursor?: string | null) => ['reconciliation', 'manual-pair-options', canonicalAccountId, side, cursor ?? null] as const,
};

function isConflict(error: unknown): boolean {
  return (error as { response?: { status?: unknown } } | null)?.response?.status === 409;
}

async function invalidateReconciliationReadFamilies(queryClient: ReturnType<typeof useQueryClient>): Promise<boolean> {
  const keys = [
    reconciliationKeys.root(),
    ['accounts'],
    ['transactions'],
    ['spend-by-category'],
    ['spend-by-merchant'],
  ];
  const settled = await Promise.allSettled(keys.map((queryKey) => queryClient.invalidateQueries(
    { queryKey, refetchType: 'active' },
    { throwOnError: true },
  )));
  return settled.every((result) => result.status === 'fulfilled');
}

function parseMutationEnvelope<T extends z.ZodTypeAny, M extends z.ZodTypeAny>(payload: unknown, dataSchema: T, metaSchema: M): z.infer<T> {
  const parsed = parseEnvelope(dataSchema, payload);
  metaSchema.parse(parsed.meta);
  return parsed.data;
}

function useReconciliationMutation<TData, TVariables>(
  options: ReconciliationMutationOptions,
  mutationFn: (variables: TVariables) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  return useMutation<TData, unknown, TVariables>({
    mutationFn,
    retry: false,
    onSuccess: async () => {
      if (!await invalidateReconciliationReadFamilies(queryClient)) {
        options.onStaleReselect({ kind: 'stale', reason: 'refresh_failed' });
      }
    },
    onError: async (error) => {
      if (isConflict(error)) {
        const refreshed = await invalidateReconciliationReadFamilies(queryClient);
        options.onStaleReselect({ kind: 'stale', reason: refreshed ? 'changed_since' : 'refresh_failed' });
      }
    },
  });
}

function parseAccountPairForDecision(pair: AccountPair): AccountPair {
  const parsed = accountPairSchema.parse(pair);
  if (!parsed.can_decide || parsed.expected_generation == null) {
    throw new Error('account pair is not currently decidable');
  }
  return parsed;
}

function parseAccountPairForReverse(pair: AccountPair): AccountPair {
  const parsed = accountPairSchema.parse(pair);
  if (parsed.decision_id == null || parsed.decision_version == null || parsed.expected_generation == null) {
    throw new Error('account pair is missing reverse decision concurrency fields');
  }
  return parsed;
}

export function useAccountDecision(options: ReconciliationMutationOptions) {
  return useReconciliationMutation(options, async (input: AccountDecisionInput) => {
    const pair = parseAccountPairForDecision(input.pair);
    const body = accountDecisionSchema.parse({
      old_account_id: pair.old_account_id,
      new_account_id: pair.new_account_id,
      decision: input.decision,
      expected_version: pair.decision_version,
      expected_generation: pair.expected_generation,
    });
    const response = await client.post(`/api/reconciliation/items/${input.replacementItemId}/account-decisions`, body);
    return parseMutationEnvelope(response.data, decisionSchema, emptyMutationMetaSchema);
  });
}

export function useAccountDecisionBatch(options: ReconciliationMutationOptions) {
  return useReconciliationMutation(options, async (input: AccountDecisionBatchInput) => {
    if (input.selections.length < 1 || input.selections.length > 20) {
      throw new Error('account decision batch must contain 1 to 20 selections');
    }
    const pairs = input.selections.map(({ pair }) => parseAccountPairForDecision(pair));
    const expectedGeneration = pairs[0].expected_generation;
    if (expectedGeneration == null) {
      throw new Error('account decision batch is missing generation');
    }
    if (pairs.some((pair) => pair.expected_generation !== expectedGeneration)) {
      throw new Error('account decision batch generations must match');
    }
    const body = accountDecisionBatchSchema.parse({
      expected_generation: expectedGeneration,
      decisions: input.selections.map(({ decision }, index) => accountDecisionCommandSchema.parse({
        old_account_id: pairs[index].old_account_id,
        new_account_id: pairs[index].new_account_id,
        decision,
        expected_version: pairs[index].decision_version,
      })),
    });
    const response = await client.post(`/api/reconciliation/items/${input.replacementItemId}/account-decisions/batch`, body);
    return parseMutationEnvelope(response.data, z.array(decisionSchema), accountDecisionBatchMetaSchema);
  });
}

export function useReverseAccountDecision(options: ReconciliationMutationOptions) {
  return useReconciliationMutation(options, async (input: ReverseAccountDecisionInput) => {
    const pair = parseAccountPairForReverse(input.pair);
    const body = reverseDecisionSchema.parse({
      expected_generation: pair.expected_generation,
      expected_version: pair.decision_version,
    });
    const response = await client.post(`/api/reconciliation/items/${input.replacementItemId}/account-decisions/${pair.decision_id}/reverse`, body);
    return parseMutationEnvelope(response.data, decisionSchema, emptyMutationMetaSchema);
  });
}

export function useFinishReconciliationReview(options: ReconciliationMutationOptions) {
  return useReconciliationMutation(options, async (input: FinishReviewInput) => {
    const status = reviewStatusSchema.parse(input.status);
    if (status.review_generation == null) {
      throw new Error('review status is missing review generation');
    }
    const body = finishReviewSchema.parse({ expected_generation: status.review_generation });
    const response = await client.post(`/api/reconciliation/items/${input.replacementItemId}/finish-review`, body);
    return parseMutationEnvelope(response.data, reviewStatusSchema, emptyMutationMetaSchema);
  });
}

function parseTransactionPairForDecision(pair: TransactionPair): TransactionPair {
  const parsed = transactionPairSchema.parse(pair);
  if (parsed.expected_generation == null) {
    throw new Error('transaction pair is missing generation');
  }
  return parsed;
}

function transactionDecisionBody(
  pair: TransactionPair,
  decision: 'same' | 'distinct',
  annotationSelection?: AnnotationSelection,
) {
  const parsed = parseTransactionPairForDecision(pair);
  if (decision === 'distinct' && annotationSelection != null) {
    throw new Error('distinct decisions cannot select an annotation anchor');
  }
  if (parsed.annotation_options.length !== 2 && annotationSelection != null) {
    throw new Error('annotation selection is only valid for two options');
  }
  if (parsed.annotation_options.length === 2 && decision === 'same') {
    if (annotationSelection == null) {
      throw new Error('two annotation options require a selection');
    }
    const matchingOption = parsed.annotation_options.find((option) => (
      option.anchor_transaction_id === annotationSelection.anchor_transaction_id
      && option.fingerprint === annotationSelection.fingerprint
    ));
    if (matchingOption == null) {
      throw new Error('annotation selection does not match a server option');
    }
  }
  const body = {
    old_transaction_id: parsed.old_transaction_id,
    new_transaction_id: parsed.new_transaction_id,
    decision,
    expected_version: parsed.decision_version,
    old_source_tip_id: parsed.old_source_tip_id,
    new_source_tip_id: parsed.new_source_tip_id,
    expected_generation: parsed.expected_generation,
    ...(decision === 'same' && parsed.annotation_options.length === 2 && annotationSelection != null
      ? {
        annotation_anchor_transaction_id: annotationSelection.anchor_transaction_id,
        annotation_fingerprint: annotationSelection.fingerprint,
      }
      : {}),
  };
  return transactionDecisionSchema.parse(body);
}

export function useTransactionDecision(options: ReconciliationMutationOptions) {
  return useReconciliationMutation(options, async (input: TransactionDecisionInput) => {
    const body = transactionDecisionBody(input.pair, input.decision, input.annotationSelection);
    const response = await client.post(`/api/reconciliation/items/${input.replacementItemId}/transaction-decisions`, body);
    return parseMutationEnvelope(response.data, decisionSchema, emptyMutationMetaSchema);
  });
}

export function useTransactionDecisionBatch(options: ReconciliationMutationOptions) {
  return useReconciliationMutation(options, async (input: TransactionDecisionBatchInput) => {
    if (input.selections.length < 1 || input.selections.length > 50) {
      throw new Error('transaction decision batch must contain 1 to 50 selections');
    }
    const parsedPairs = input.selections.map(({ pair }) => parseTransactionPairForDecision(pair));
    const expectedGeneration = parsedPairs[0].expected_generation;
    if (expectedGeneration == null || parsedPairs.some((pair) => pair.expected_generation !== expectedGeneration)) {
      throw new Error('transaction decision batch generations must match');
    }
    const body = transactionDecisionBatchSchema.parse({
      expected_generation: expectedGeneration,
      decisions: input.selections.map(({ decision, annotationSelection }, index) => {
        const command = transactionDecisionBody(parsedPairs[index], decision, annotationSelection);
        const { expected_generation: _expectedGeneration, ...withoutGeneration } = command;
        return withoutGeneration;
      }),
    });
    const response = await client.post(`/api/reconciliation/items/${input.replacementItemId}/transaction-decisions/batch`, body);
    return parseMutationEnvelope(response.data, z.array(decisionSchema), accountDecisionBatchMetaSchema);
  });
}

export function useReverseTransactionDecision(options: ReconciliationMutationOptions) {
  return useReconciliationMutation(options, async (input: ReverseTransactionDecisionInput) => {
    const pair = parseTransactionPairForDecision(input.pair);
    if (pair.decision_id == null || pair.decision_version == null) {
      throw new Error('transaction pair is missing reverse decision concurrency fields');
    }
    const body = reverseDecisionSchema.parse({
      expected_generation: pair.expected_generation,
      expected_version: pair.decision_version,
    });
    const response = await client.post(`/api/reconciliation/items/${input.replacementItemId}/transaction-decisions/${pair.decision_id}/reverse`, body);
    return parseMutationEnvelope(response.data, decisionSchema, emptyMutationMetaSchema);
  });
}

export function useManualPairOptions(
  canonicalAccountId: string | null | undefined,
  side: ManualPairSide,
  cursor?: string | null,
) {
  return useQuery({
    queryKey: canonicalAccountId
      ? reconciliationKeys.manualPairOptions(canonicalAccountId, side, cursor)
      : ['reconciliation', 'manual-pair-options', 'disabled', side, cursor ?? null],
    enabled: Boolean(canonicalAccountId),
    retry: false,
    queryFn: async () => {
      const params = {
        canonical_account_id: canonicalAccountId,
        side,
        ...(cursor != null ? { cursor } : {}),
        limit: 50,
      };
      const response = await client.get('/api/reconciliation/transaction-candidates/manual-pair-options', { params });
      const parsed = parseEnvelope(z.array(manualPairTipSchema), response.data);
      const meta = manualPairOptionsMetaSchema.parse(parsed.meta);
      if (meta.side !== side) {
        throw new Error('manual pair response side does not match request');
      }
      return { data: parsed.data, meta };
    },
  });
}

export function useManualTransactionPair(options: ReconciliationMutationOptions) {
  return useReconciliationMutation(options, async (input: ManualPairInput) => {
    const expectedGeneration = generationSchema.parse(input.expectedGeneration);
    const oldTip = manualPairTipSchema.parse(input.oldTip);
    const newTip = manualPairTipSchema.parse(input.newTip);
    const body = manualTransactionPairSchema.parse({
      old_transaction_id: oldTip.transaction_id,
      new_transaction_id: newTip.transaction_id,
      old_source_tip_id: oldTip.source_tip_id,
      new_source_tip_id: newTip.source_tip_id,
      expected_generation: expectedGeneration,
    });
    const response = await client.post(`/api/reconciliation/items/${input.replacementItemId}/manual-transaction-pairs`, body);
    return parseMutationEnvelope(response.data, z.array(transactionPairSchema), manualPairMutationMetaSchema);
  });
}

export function useAccountPairs(replacementItemId: string | null | undefined, cursor?: string | null) {
  return useQuery({
    queryKey: replacementItemId ? reconciliationKeys.accountPairs(replacementItemId, cursor) : ['reconciliation', 'account-pairs', 'disabled'],
    enabled: Boolean(replacementItemId),
    retry: false,
    queryFn: async () => {
      const response = await client.get(`/api/reconciliation/items/${replacementItemId}/account-pairs`, { params: { cursor: cursor ?? undefined } });
      const parsed = parseEnvelope(z.array(accountPairSchema), response.data);
      return { data: parsed.data, meta: accountPairsMetaSchema.parse(parsed.meta) };
    },
  });
}

export function useTransactionCandidates(canonicalAccountId: string | null | undefined, cursor?: string | null) {
  return useQuery({
    queryKey: canonicalAccountId ? reconciliationKeys.transactionCandidates(canonicalAccountId, cursor) : ['reconciliation', 'transaction-candidates', 'disabled'],
    enabled: Boolean(canonicalAccountId),
    retry: false,
    queryFn: async () => {
      const response = await client.get('/api/reconciliation/transaction-candidates', { params: { canonical_account_id: canonicalAccountId, cursor: cursor ?? undefined } });
      const parsed = parseEnvelope(z.array(transactionPairSchema), response.data);
      return { data: parsed.data, meta: transactionCandidatesMetaSchema.parse(parsed.meta) };
    },
  });
}

export function useReviewStatus(replacementItemId: string | null | undefined) {
  return useQuery({
    queryKey: replacementItemId ? reconciliationKeys.reviewStatus(replacementItemId) : ['reconciliation', 'review-status', 'disabled'],
    enabled: Boolean(replacementItemId),
    retry: false,
    queryFn: async () => {
      const response = await client.get(`/api/reconciliation/items/${replacementItemId}/review-status`);
      const parsed = parseEnvelope(reviewStatusSchema, response.data);
      return { data: parsed.data, meta: reviewStatusMetaSchema.parse(parsed.meta) };
    },
  });
}
